import React, { useState, useRef, ReactNode } from 'react';
import styled from 'styled-components';
import KitIcon from './KitIcon';
import KitTips from './KitTips';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import { AriaSortDir, SortDir } from '../common/types';

const sortColors = {
  on: colors.orange_50,
  off: colors.gray_40,
};

const StyledTable = styled.table<{ stickyHeader: boolean; stickyOffset: number }>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-weight: ${constants.fontWeight.normal};
  border: 1px solid ${colors.blue_20};
  box-shadow: 0 2px 6px 0 rgba(51, 50, 49, 0.35);
  width: 100%;
  text-indent: initial;
  border-spacing: 0;
  border-collapse: collapse;

  tr {
    display: table-row;
    vertical-align: inherit;
    border-color: inherit;

    th {
      background-color: ${({ theme }) => theme.page.header.bg};
      ${({ stickyHeader, stickyOffset }) =>
    stickyHeader &&
    `
          position: sticky;
          top: ${stickyOffset || 0}px;
        `}
      font-size: ${constants.fontSize.text_14}rem;
      color: ${({ theme }) => theme.page.body.text};
      border-left: none;
      border-right: none;
      border-bottom: ${constants.border.width}px solid ${colors.blue_20};
      padding: ${constants.spacing.m}rem ${constants.spacing.sm}rem;
      text-align: inherit;

      &:hover {
        color: darken(0.2, ${({ theme }) => theme.page.body.text});
      }

      &:first-of-type {
        padding-left: ${constants.spacing.absolute.l}px;
      }

      &:last-of-type {
        padding-right: ${constants.spacing.absolute.l}px;
      }
    }
  }

  thead {
    display: table-header-group;
    vertical-align: middle;
    border-color: inherit;
  }

  tbody {
    display: table-row-group;
    vertical-align: middle;
    border-color: inherit;

    tr {
      font-size: ${constants.fontSize.text_14}rem;

      &:hover {
        background-color: ${({ theme }) => theme.card.body.bg};
      }

      td {
        border: 1px solid ${colors.blue_20};
        border-left: none;
        border-right: none;
        padding: ${constants.spacing.m}rem ${constants.spacing.sm}rem;
        vertical-align: baseline;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      td:first-of-type {
        padding-left: ${constants.spacing.absolute.l}px;
      }
      td:last-of-type {
        padding-right: ${constants.spacing.absolute.l}px;
      }
    }
  }
`;

interface StyledTableHeaderProps {
  active?: boolean;
  sortable?: boolean;
}

const StyledTableHeader = styled.th<StyledTableHeaderProps>`
  &&& {
    color: ${(props) => (props.active ? `${colors.gray_90}` : `${colors.gray_50}`)};
    cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};
  }
  &.column-description {
    overflow: visible;
    button span {
      border-bottom: 2px dashed ${colors.gray_50};
    }
  }
}
`;

const TableHeaderContainer = styled.button<{
  sortable: boolean;
}>`
  display: flex;
  height: 100%;
  width: 100%;
  font-weight: bold;
  font-size: ${constants.fontSize.text_14}rem;
  color: ${({ theme }) => theme.page.body.text};
  background-color: transparent;
  justify-content: ${(props) => (props.sortable ? 'space-between' : 'center')};
  align-items: center;
  border: none;
  &:focus {
    box-shadow: 0 0 0 ${constants.border.width}px ${({ theme }) => theme.focus};
    color: ${({ theme }) => theme.page.body.text};
  }
  &:hover {
    color: ${({ theme }) => theme.page.body.text};
  }
`;

const TableHeaderName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: ${constants.fontSize.text_14}rem;
`;

const SortContainer = styled.div`
  display: flex;
  flex-direction: column;
  div:last-child {
    margin-top: -${constants.spacing.absolute.s}px;
  }
`;

export interface TableHeaderProps {
  text: string; // name of the column
  sortable: boolean;
  active?: boolean;
  children?: string | JSX.Element | JSX.Element[];
  className?: string;
  onSortClicked?: (sortDir: number) => void;
  qaId?: string;
  columnDescription?: ReactNode;
}

function getSortFill(targetDir: number, currentSortDir: number, active: boolean) {
  if (active && targetDir === currentSortDir) {
    return sortColors.on;
  }
  return sortColors.off;
}

const TableHeader = (props: TableHeaderProps) => {
  // Used to show full column header if header is cut off
  const [showOverlay, setShowOverlay] = useState(false);
  const [sortDir, setSortDir] = useState<number>(null);
  const overlayTarget = useRef(null);

  const ariaSortLabel: AriaSortDir = (() => {
    if (sortDir === null) {
      return 'none';
    }
    return sortDir === SortDir.DESC ? 'descending' : 'ascending';
  })();

  const showHideKitOverlay = (evt) => {
    // If column has a description, show overlay
    if (props.columnDescription) {
      return setShowOverlay(true);
    }
    // Checks whether column header overflows space in column
    // If there is overflow, the overlay is shown
    evt.stopPropagation();
    const { target } = evt;
    if (target) {
      const childContainerDiv = target.querySelector('button');
      if (childContainerDiv) {
        const span = childContainerDiv.getElementsByTagName('span')[0];
        if (span) {
          if (span.offsetWidth < span.scrollWidth) {
            setShowOverlay(true);
          }
        }
      }
    }
  };

  const onSortClicked = () => {
    if (!props.sortable || !props.onSortClicked) {
      return;
    }
    const newSortDir = (() => {
      if (sortDir === null || !props.active) {
        return SortDir.DESC;
      }
      return sortDir === SortDir.DESC ? SortDir.ASC : null;
    })();

    props.onSortClicked(newSortDir);
    setSortDir(newSortDir);
  };

  const tableHeaderContainer = (
    <TableHeaderContainer
      onClick={onSortClicked}
      sortable={props.sortable}
      tabIndex={0}
      role='button'
    >
      <TableHeaderName>{props.children || props.text}</TableHeaderName>
      {props.sortable && (
        <SortContainer>
          <KitIcon icon='caret-up' fill={getSortFill(SortDir.ASC, sortDir, props.active)} />
          <KitIcon icon='caret-down' fill={getSortFill(SortDir.DESC, sortDir, props.active)} />
        </SortContainer>
      )}
    </TableHeaderContainer>
  );

  const overlayWrapper = showOverlay ? (
    <KitTips content={props.columnDescription ? props.columnDescription : props.text}>
      {tableHeaderContainer}
    </KitTips>
  ) : (
    tableHeaderContainer
  );

  return (
    <StyledTableHeader
      aria-sort={ariaSortLabel}
      className={props.className}
      data-place='bottom'
      onKeyDown={showHideKitOverlay}
      onMouseEnter={showHideKitOverlay}
      onMouseLeave={() => setShowOverlay(false)}
      ref={overlayTarget}
      active={props.active}
      sortable={props.sortable}
      data-qa-id={props.qaId}
    >
      {props.text && overlayWrapper}
    </StyledTableHeader>
  );
};

export interface KitTableProps {
  children?: ReactNode;
  className?: string;
  role?: string;
  stickyHeader?: boolean;
  stickyOffset?: number;
}

function KitTable({
  children,
  className,
  role = 'table',
  stickyHeader = false,
  stickyOffset = 0,
}: KitTableProps): JSX.Element {
  return (
    <StyledTable
      role={role}
      className={className}
      stickyHeader={stickyHeader}
      stickyOffset={stickyOffset}
    >
      {children}
    </StyledTable>
  );
}

KitTable.Th = TableHeader;

export default KitTable;
